import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency, getRecommendationTypeIcon } from "@/lib/utils";
import { OptimizationRecommendation } from "@shared/schema";
import { ArrowRight, ArrowUpRight, DollarSign, FolderOpen, Lightbulb, Trash, ArrowUpCircle } from "lucide-react";
import { useState } from "react";
import RecommendationDetailDialog from "../cost-management/recommendation-detail-dialog";

function RecommendationItem({ 
  recommendation, 
  onSelect 
}: { 
  recommendation: OptimizationRecommendation;
  onSelect: (id: number) => void;
}) {
  const { iconName, color } = getRecommendationTypeIcon(recommendation.title);
  
  // Map the icon name to the actual Lucide icon component
  const getIcon = (name: string) => {
    switch (name) {
      case 'DollarSign': return <DollarSign className="h-5 w-5" />;
      case 'FolderOpen': return <FolderOpen className="h-5 w-5" />;
      case 'Trash': return <Trash className="h-5 w-5" />;
      case 'ArrowUpCircle': return <ArrowUpCircle className="h-5 w-5" />;
      default: return <Lightbulb className="h-5 w-5" />;
    }
  };
  
  return (
    <div 
      className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors cursor-pointer group"
      onClick={() => onSelect(recommendation.id)}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center`}>
            {getIcon(iconName)}
          </div>
        </div>
        <div className="ml-4 flex-1">
          <h4 className="text-sm font-medium text-slate-800">{recommendation.title}</h4>
          <p className="text-xs text-slate-500 mt-1">{recommendation.description}</p>
          <div className="mt-2 flex items-center text-xs text-green-600 font-medium">
            <ArrowUpRight className="h-3.5 w-3.5 mr-1" />
            Potential savings: {formatCurrency(recommendation.potentialSavings)}/month
          </div>
        </div>
        <div className="self-center">
          <div className="text-primary group-hover:text-primary/80 flex items-center text-xs font-medium">
            View Details
            <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OptimizationRecommendations() {
  const [selectedRecommendationId, setSelectedRecommendationId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, isLoading, error } = useQuery<OptimizationRecommendation[]>({
    queryKey: ['/api/recommendations'],
  });

  const recommendations: OptimizationRecommendation[] = data || [];
  const recommendationCount = recommendations.length;

  const handleViewRecommendation = (id: number) => {
    setSelectedRecommendationId(id);
    setDialogOpen(true);
  };

  return (
    <>
      <Card className="h-full">
        <CardHeader className="flex flex-row items-center justify-between border-b border-slate-200 pb-4">
          <CardTitle className="text-lg font-semibold">Cost Optimization</CardTitle>
          <span className="text-xs font-semibold px-2 py-1 bg-green-100 text-green-800 rounded-full">
            {isLoading ? <Skeleton className="h-4 w-8 inline-block" /> : `${recommendationCount} Recommendations`}
          </span>
        </CardHeader>
        <CardContent className="p-6 flex-1">
          <div className="space-y-4">
            {isLoading ? (
              [...Array(3)].map((_, index) => (
                <div key={index} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="ml-4 flex-1">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-full mt-1" />
                      <Skeleton className="h-3 w-1/2 mt-2" />
                    </div>
                  </div>
                </div>
              ))
            ) : error ? (
              <div className="text-error">Error loading recommendations</div>
            ) : (
              <>
                {recommendations.slice(0, 3).map((recommendation) => (
                  <RecommendationItem 
                    key={recommendation.id} 
                    recommendation={recommendation} 
                    onSelect={handleViewRecommendation}
                  />
                ))}
                
                <Button 
                  asChild
                  variant="outline" 
                  className="w-full py-2 text-xs font-medium text-primary border border-primary rounded-md hover:bg-primary/5"
                >
                  <Link to="/cost-management">
                    View All Recommendations
                  </Link>
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <RecommendationDetailDialog
        recommendationId={selectedRecommendationId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  );
}
